import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EntityHistoryService } from './services/entity-history.service';
import { refMapping } from './refMapping';
import { UtilitiesService } from '../../services/utilities.service';
import * as diff_match_patch from 'diff-match-patch';

interface FlattenedChange {
  id: string;
  date: Date;
  userName: string;
  source: string;
  /** Name of the automation that performed the change, when one did. */
  automation?: string;
  /** The person who triggered the automation, when the change was not made directly. */
  triggeredByName?: string;
  action: string;
  fieldName: string;
  originalValue: any;
  newValue: any;
  originalValueText: string;
  newValueText: string;
  originalValueHtml: string;
  newValueHtml: string;
  hasOriginalValue: boolean;
  isOriginalDate: boolean;
  isNewDate: boolean;
  isHighlighted: boolean;
  fieldType: string;
  inlineDiffHtml?: string;
  arrayDiffHtml?: string;
  sessionKey: string;
}

interface SessionGroup {
  sessionKey: string;
  date: Date;
  userName: string;
  source: string;
  automation?: string;
  triggeredByName?: string;
  changes: FlattenedChange[];
  isExpanded: boolean;
}

interface DropdownOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-entity-history',
  templateUrl: './entity-history.component.html',
  styleUrls: ['./entity-history.component.sass'],
  encapsulation: ViewEncapsulation.None
})
export class EntityHistoryComponent implements OnInit {
  @Input() isDynamicEntity: boolean = false;

  // Route parameters
  entityId: any;
  entityType: any;
  entityName: string = '';

  // Data properties
  entityData: any[] = [];
  flattenedData: FlattenedChange[] = [];
  filteredData: FlattenedChange[] = [];
  totalChanges: number = 0;

  // Stats
  sessionCount: number = 0;
  lastModified: Date | null = null;

  // Display mode
  displayMode: 'grouped' | 'flat' = 'grouped';
  groupedData: SessionGroup[] = [];
  expandedRows: Set<string> = new Set();

  // Search and filter properties
  searchTerm: string = '';
  selectedField: string = '';
  selectedUser: string = '';
  selectedAction: string = '';

  // Sorting properties (used in flat mode)
  sortField: string = 'date';
  sortOrder: number = -1;

  get hasActiveFilters(): boolean {
    return !!(this.searchTerm || this.selectedField || this.selectedUser || this.selectedAction);
  }

  get isDefaultSort(): boolean {
    return this.sortField === 'date' && this.sortOrder === -1;
  }

  // Dropdown options
  fieldOptions: DropdownOption[] = [];
  userOptions: DropdownOption[] = [];
  actionOptions: DropdownOption[] = [
    { label: 'Add', value: 'add' },
    { label: 'Replace', value: 'replace' },
    { label: 'Remove', value: 'remove' }
  ];

  // Search timeout for debouncing
  private searchTimeout: any;

  constructor (
    private route: ActivatedRoute,
    private entityHistoryService: EntityHistoryService,
    private utilitiesService: UtilitiesService
  ) { }

  async ngOnInit() {
    const routeParams = this.route.snapshot.paramMap;
    this.entityId = routeParams.get('id');
    this.entityType = routeParams.get('entityType');
    await this.getEntityDetails();
    await this.getEntityHistory();
    await this.getRefsFromMapping();
    this.processData();
  }

  async getEntityDetails() {
    if (this.entityId && this.entityType) {
      try {
        const response = await this.utilitiesService.getAndReturnOptions({
          entity: this.entityType,
          query: { id: this.entityId },
          select: 'name',
          autopopulate: false,
          populate: [],
          populateMinimal: true,
          smartPopulate: {},
          virtuals: false,
          sort: { id: -1 },
        });

        this.entityName = response && response.length > 0 ? response[0].name : 'Unknown Entity';
      } catch (error) {
        console.error('Error fetching entity details:', error);
        this.entityName = 'Unknown Entity';
      }
    }
  }

  async getEntityHistory() {
    if (this.entityId && this.entityType) {
      let response = await this.entityHistoryService[this.isDynamicEntity ? 'getDynamicEntityHistory' : 'getEntityHistory'](this.entityType, this.entityId);

      if (response) {
        this.entityData = this.filterAndConvertChanges(response);
      }
    }
  }

  private filterAndConvertChanges(data: any[]): any[] {
    let filteredData = data.map((record: { ops: any[]; }) => {
      record.ops = record.ops.filter(op => {
        if (op.path.startsWith('/userData')) return false;

        if (op.op === 'add' && op.value === null && (!op.hasOwnProperty('originalValue') || op.originalValue === null)) {
          return false;
        }

        const hasBuffer = (value: any): boolean => {
          if (!value) return false;
          if (typeof value === 'object') {
            if (value.type === 'Buffer') return true;
            if (Array.isArray(value)) return value.some(item => hasBuffer(item));
            return Object.values(value).some(val => hasBuffer(val));
          }
          return false;
        };

        if (hasBuffer(op.value) || hasBuffer(op.originalValue)) return false;

        if (op.path.startsWith('/_automations')) {
          if ((op.op === 'add' || op.op === 'replace') && op.value) {
            op.originalValue = null;
            op.path = '/Automation';
            return true;
          }
          return false;
        }

        return true;
      });
      return record;
    });

    return filteredData.filter((record: { ops: string | any[]; }) => record.ops.length > 0);
  }

  /**
   * Who a history row should be credited to.
   *
   * For an automated change the automation is what performed the write, but the person who
   * set it off is the more useful thing to show — so the row reads as that person, with the
   * automation named alongside it. A row with no provenance at all is a write path that is
   * not stamping it; that is a bug to chase, not a display problem, so it is labelled
   * plainly rather than hidden behind "Undefined".
   */
  private attributionName(userData: any): string {
    if (!userData) return 'Not recorded';
    if (userData.actorType === 'automation') {
      return userData.triggeredBy?.name || userData.triggeredBy?.email || userData.name || 'CMS Automation';
    }
    return userData.name || userData.email || 'Not recorded';
  }

  /** One-line attribution for a row, e.g. "Ada Lovelace via Collection sync". */
  attributionLabel(change: FlattenedChange): string {
    if (!change.automation) return change.userName;
    return change.triggeredByName
      ? `${change.triggeredByName} via ${change.automation}`
      : `${change.automation} (no triggering user recorded)`;
  }

  private processData() {
    this.flattenedData = [];
    const fieldsSet = new Set<string>();
    const usersSet = new Set<string>();

    this.entityData.forEach((record, recordIndex) => {
      this.groupArrayIndexOps(record.ops).forEach((op: any, opIndex: number) => {
        const fieldType = this.detectFieldType(op.originalValue, op.value);

        const change: FlattenedChange = {
          id: `${recordIndex}-${opIndex}`,
          date: new Date(record.date),
          // Provenance now lives on `userData`, written once at the time of the change.
          // `changeMetadata.editSource` is gone: it was computed from the patch row rather
          // than the entity, so it read 'untracked' on every creation.
          userName: this.attributionName(record.userData),
          source: record.userData?.editSource || '',
          automation: record.userData?.automation || undefined,
          triggeredByName: record.userData?.triggeredBy?.name
            || record.userData?.triggeredBy?.email
            || undefined,
          action: op.op,
          fieldName: this.formatFieldName(op.path),
          originalValue: op.originalValue,
          newValue: op.value,
          originalValueText: this.getValueText(op.originalValue),
          newValueText: this.getValueText(op.value),
          originalValueHtml: '',
          newValueHtml: '',
          hasOriginalValue: op.hasOwnProperty('originalValue') && op.originalValue !== null,
          isOriginalDate: this.isDate(op.originalValue),
          isNewDate: this.isDate(op.value),
          isHighlighted: false,
          fieldType,
          sessionKey: new Date(record.date).getTime().toString(),
        };

        change.originalValueHtml = this.processValueForDisplay(op.originalValue);
        change.newValueHtml = this.processValueForDisplay(op.value);

        if (fieldType === 'string' && op.op === 'replace'
            && typeof op.originalValue === 'string' && typeof op.value === 'string') {
          change.inlineDiffHtml = this.computeInlineDiff(op.originalValue, op.value);
        }
        if (fieldType === 'array' && op.op === 'replace'
            && Array.isArray(op.originalValue) && Array.isArray(op.value)) {
          change.arrayDiffHtml = this.computeArrayDiff(op.originalValue, op.value);
        }

        this.flattenedData.push(change);
        fieldsSet.add(change.fieldName);
        usersSet.add(change.userName);
      });
    });

    this.flattenedData.sort((a, b) => b.date.getTime() - a.date.getTime());

    this.fieldOptions = Array.from(fieldsSet).sort().map(field => ({ label: field, value: field }));
    this.userOptions = Array.from(usersSet).sort().map(user => ({ label: user, value: user }));

    this.totalChanges = this.flattenedData.length;
    this.sessionCount = new Set(this.flattenedData.map(c => c.sessionKey)).size;
    this.lastModified = this.flattenedData.length > 0
      ? new Date(Math.max(...this.flattenedData.map(c => c.date.getTime())))
      : null;

    this.filteredData = [...this.flattenedData];
    this.rebuildGroupedData(this.filteredData);
  }

  private rebuildGroupedData(filtered: FlattenedChange[]) {
    const groupMap = new Map<string, SessionGroup>();
    filtered.forEach(c => {
      if (!groupMap.has(c.sessionKey)) {
        groupMap.set(c.sessionKey, {
          sessionKey: c.sessionKey,
          date: c.date,
          userName: c.userName,
          source: c.source,
          automation: c.automation,
          triggeredByName: c.triggeredByName,
          changes: [],
          isExpanded: true,
        });
      }
      groupMap.get(c.sessionKey)!.changes.push(c);
    });
    this.groupedData = Array.from(groupMap.values())
      .sort((a, b) => Number(b.sessionKey) - Number(a.sessionKey));
  }

  private formatFieldName(path: string): string {
    let fieldName = path.startsWith('/') ? path.slice(1) : path;
    if (fieldName.endsWith('_ref')) {
      fieldName = fieldName.slice(0, -4);
    }
    return fieldName;
  }

  private getValueText(value: any): string {
    if (value === null || value === undefined) return 'Null';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  private processValueForDisplay(value: any): string {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) {
      return `<pre class="json-block">${this.escapeHtml(JSON.stringify(value, null, 2))}</pre>`;
    }
    if (typeof value === 'object') {
      return this.formatJsonWithSyntax(value);
    }
    return String(value);
  }

  private escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private formatJsonWithSyntax(value: any): string {
    const json = JSON.stringify(value, null, 2);
    const highlighted = json.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match) => {
        if (/^"/.test(match)) {
          if (/:$/.test(match)) return `<span class="json-key">${this.escapeHtml(match)}</span>`;
          return `<span class="json-string">${this.escapeHtml(match)}</span>`;
        }
        if (/true|false|null/.test(match)) return `<span class="json-boolean">${match}</span>`;
        return `<span class="json-number">${match}</span>`;
      }
    );
    return `<pre class="json-block">${highlighted}</pre>`;
  }

  detectFieldType(originalValue: any, newValue: any): string {
    const isRef = (v: any) => typeof v === 'string' && v.startsWith('<a ');
    if (isRef(originalValue) || isRef(newValue)) return 'ref';
    if (typeof originalValue === 'boolean' || typeof newValue === 'boolean') return 'boolean';
    if (typeof originalValue === 'number' || typeof newValue === 'number') return 'number';
    if (Array.isArray(originalValue) || Array.isArray(newValue)) return 'array';
    if ((originalValue !== null && typeof originalValue === 'object') ||
        (newValue !== null && typeof newValue === 'object')) return 'object';
    if (this.isDate(originalValue) || this.isDate(newValue)) return 'date';
    if (typeof originalValue === 'string' || typeof newValue === 'string') return 'string';
    return 'unknown';
  }

  formatDisplayLabel(value: string): string {
    if (!value) return value;
    return value
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  computeInlineDiff(original: string, next: string): string {
    const dmp = new diff_match_patch();
    const diffs = dmp.diff_main(original, next);
    dmp.diff_cleanupSemantic(diffs);

    // If more than 60% of characters are changed, the diff is too noisy — return empty
    // so the template falls back to plain before/after boxes.
    const totalChars = diffs.reduce((sum, [, text]) => sum + text.length, 0);
    const changedChars = diffs.reduce((sum, [op, text]) => op !== 0 ? sum + text.length : sum, 0);
    if (totalChars > 0 && changedChars / totalChars > 0.6) return '';

    return diffs.map(([op, text]) => {
      const escaped = this.escapeHtml(text);
      if (op === -1) return `<del>${escaped}</del>`;
      if (op === 1) return `<ins>${escaped}</ins>`;
      return escaped;
    }).join('');
  }

  /**
   * mongoose-patch-history (via fast-json-patch) diffs arrays index-by-index instead of emitting
   * one whole-array replace, so e.g. a 3-element colors_ref change arrives as three separate ops
   * at /colors_ref/0, /colors_ref/1, /colors_ref/2. Collapse ops that share an array-index path
   * into a single synthetic array-replace op so they render as one field with a chip diff instead
   * of N disconnected scalar rows. A field with only one touched index is left as-is.
   */
  private groupArrayIndexOps(ops: any[]): any[] {
    const indexPathPattern = /^\/(.+)\/(\d+)$/;
    const opsByBasePath = new Map<string, any[]>();

    ops.forEach(op => {
      const match = typeof op.path === 'string' ? op.path.match(indexPathPattern) : null;
      if (!match) return;
      const basePath = '/' + match[1];
      if (!opsByBasePath.has(basePath)) opsByBasePath.set(basePath, []);
      opsByBasePath.get(basePath)!.push(op);
    });

    const consumed = new Set<any>();
    opsByBasePath.forEach(groupOps => {
      if (groupOps.length > 1) groupOps.forEach(op => consumed.add(op));
    });

    const emittedBasePaths = new Set<string>();
    const result: any[] = [];

    ops.forEach(op => {
      if (!consumed.has(op)) {
        result.push(op);
        return;
      }

      const basePath = '/' + op.path.match(indexPathPattern)[1];
      if (emittedBasePaths.has(basePath)) return;
      emittedBasePaths.add(basePath);

      const groupOps = opsByBasePath.get(basePath)!;
      const removed = groupOps
        .filter(o => o.hasOwnProperty('originalValue') && o.originalValue !== null && o.originalValue !== undefined)
        .map(o => this.stripHtml(o.originalValue));
      const added = groupOps
        .filter(o => o.value !== null && o.value !== undefined)
        .map(o => this.stripHtml(o.value));

      result.push({ op: 'replace', path: basePath, value: added, originalValue: removed });
    });

    return result;
  }

  private stripHtml(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).replace(/<[^>]+>/g, '');
  }

  computeArrayDiff(original: any[], next: any[]): string {
    const toKey = (v: any) => JSON.stringify(v);
    const originalKeys = new Set(original.map(toKey));
    const nextKeys = new Set(next.map(toKey));

    const removed = original.filter(v => !nextKeys.has(toKey(v)));
    const added = next.filter(v => !originalKeys.has(toKey(v)));
    const unchanged = original.filter(v => nextKeys.has(toKey(v)));

    const chip = (cls: string, prefix: string, v: any) =>
      `<span class="diff-chip ${cls}">${prefix}${this.escapeHtml(String(v))}</span>`;

    return [
      ...removed.map(v => chip('diff-removed', '- ', v)),
      ...unchanged.map(v => chip('diff-unchanged', '', v)),
      ...added.map(v => chip('diff-added', '+ ', v)),
    ].join('');
  }

  isObjectValue(value: any): boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  isDate(value: any): boolean {
    if (value instanceof Date) return true;
    if (typeof value === 'string') {
      const isoDatePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(.\d+)?(Z|[\+\-]\d{2}:\d{2})?)?$/;
      if (isoDatePattern.test(value)) {
        const date = new Date(value);
        return !isNaN(date.getTime());
      }
    }
    return false;
  }

  getActionSeverity(action: string): string {
    switch (action) {
      case 'add': return 'success';
      case 'replace': return 'warning';
      case 'remove': return 'danger';
      default: return 'info';
    }
  }

  toggleDisplayMode() {
    this.displayMode = this.displayMode === 'grouped' ? 'flat' : 'grouped';
    this.applyFilters();
  }

  toggleSession(sessionKey: string) {
    const session = this.groupedData.find(s => s.sessionKey === sessionKey);
    if (session) session.isExpanded = !session.isExpanded;
  }

  toggleRowExpand(id: string) {
    if (this.expandedRows.has(id)) {
      this.expandedRows.delete(id);
    } else {
      this.expandedRows.add(id);
    }
  }

  isRowExpanded(id: string): boolean {
    return this.expandedRows.has(id);
  }

  onSearchChange() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applyFilters();
    }, 300);
  }

  onFilterChange() {
    this.applyFilters();
  }

  applyFilters() {
    let filtered = [...this.flattenedData];

    filtered.forEach(change => change.isHighlighted = false);

    if (this.selectedField) {
      filtered = filtered.filter(change => change.fieldName === this.selectedField);
    }
    if (this.selectedUser) {
      filtered = filtered.filter(change => change.userName === this.selectedUser);
    }
    if (this.selectedAction) {
      filtered = filtered.filter(change => change.action === this.selectedAction);
    }

    if (this.searchTerm) {
      try {
        const regex = new RegExp(this.searchTerm, 'i');
        filtered = filtered.filter(change => {
          const text = [change.userName, change.fieldName, change.originalValueText, change.newValueText, change.source, change.action].join(' ');
          const matches = regex.test(text);
          if (matches) change.isHighlighted = true;
          return matches;
        });
      } catch (e) {
        const searchLower = this.searchTerm.toLowerCase();
        filtered = filtered.filter(change => {
          const text = [change.userName, change.fieldName, change.originalValueText, change.newValueText, change.source, change.action].join(' ').toLowerCase();
          const matches = text.includes(searchLower);
          if (matches) change.isHighlighted = true;
          return matches;
        });
      }
    }

    this.filteredData = filtered;
    this.rebuildGroupedData(filtered);
  }

  clearSearch() {
    this.searchTerm = '';
    this.applyFilters();
  }

  clearAllFilters() {
    this.searchTerm = '';
    this.selectedField = '';
    this.selectedUser = '';
    this.selectedAction = '';
    this.applyFilters();
  }

  resetSorting() {
    this.sortField = 'date';
    this.sortOrder = -1;
  }

  onSort(event: any) {
    this.sortField = event.field;
    this.sortOrder = event.order;
  }

  getFieldLabel(value: string): string {
    const option = this.fieldOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  getUserLabel(value: string): string {
    const option = this.userOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  async getRefsFromMapping() {
    const mapping: any = refMapping[this.entityType];

    if (!mapping) {
      console.warn(`No reference mapping found for entity type: ${this.entityType}`);
      return;
    }

    for (const record of this.entityData) {
      for (const op of record.ops) {
        let key = op.path.split("/")[1];
        let ref = mapping[key];

        if (!ref) continue;

        if (Array.isArray(op.value) || Array.isArray(op.originalValue)) {
          const ids = Array.from(new Set([
            ...(Array.isArray(op.value) ? op.value : []),
            ...(Array.isArray(op.originalValue) ? op.originalValue : []),
          ].filter((v: any) => typeof v === 'string' || typeof v === 'number')));

          if (ids.length === 0) continue;

          const results = await this.utilitiesService.getAndReturnOptions({
            entity: ref.collection,
            query: { _id: { $in: ids } },
            populate: [],
            populateMinimal: true,
            autopopulate: false,
            smartPopulate: {},
            virtuals: false,
            select: 'id name _id',
            sort: { id: -1 }
          });

          const nameById = new Map<string, string>();
          results.forEach((r: any) => nameById.set(String(r._id), `${r.name} (${r.id})`));

          if (Array.isArray(op.value)) {
            op.value = op.value.map((id: any) => nameById.get(String(id)) || id);
          }
          if (Array.isArray(op.originalValue)) {
            op.originalValue = op.originalValue.map((id: any) => nameById.get(String(id)) || id);
          }

          continue;
        }

        if (op.value) {
          let valueRes = await this.utilitiesService.getAndReturnOptions({
            entity: ref.collection,
            query: { _id: op.value },
            populate: [],
            populateMinimal: true,
            autopopulate: false,
            smartPopulate: {},
            virtuals: false,
            select: 'id name',
            sort: {id: -1}
          });

          if (valueRes.length > 0) {
            let entity = ref.collection;
            let id = valueRes[0].id;
            let link = `/${entity}/${id}`;
            op.value = `<a href="${link}" target="_blank" style="color: #97C27A !important;">${valueRes[0].name} (${id})</a>`;
          }
        }

        if (op.originalValue) {
          let originalValueRes = await this.utilitiesService.getAndReturnOptions({
            entity: ref.collection,
            query: { _id: op.originalValue },
            populate: [],
            populateMinimal: true,
            autopopulate: false,
            smartPopulate: {},
            virtuals: false,
            select: 'id name',
            sort: {id: -1}
          });

          if (originalValueRes.length > 0) {
            let entity = ref.collection;
            let id = originalValueRes[0].id;
            let link = `/${entity}/${id}`;
            op.originalValue = `<a href="${link}" target="_blank" style="color: #97C27A !important;">${originalValueRes[0].name} (${id})</a>`;
          }
        }
      }
    }
  }
}
